import { Omit } from '@vindr/utils/helpers/common.helper';
import { BaseElement, BaseElementProps } from '../base';
import { ELEMENT_TYPE, ImageMetadata } from '@vindr/services/apis/element.api';

const ALIGN_ITEM_MAP = {
  start: 'flex-start',
  end: 'flex-end',
  center: 'center',
};

const aligntoStyle = (align, direction, type = 'justify') => {
  const x = direction === 'row' ? 1 : -1;
  const y = type === 'justify' ? 1 : -1;
  const isJustifyContent = x * y > 0; // otherwise isAlignItems
  if (isJustifyContent) {
    return `justify-content: ${align};`;
  } else {
    return `align-items: ${ALIGN_ITEM_MAP[align]};`;
  }
};

export interface ImageElementProps extends BaseElementProps {
  default_value: string;
  label: string;
  is_show_label: boolean;
  placeholder: string;
  format: string;
  multiple: boolean;
  metadata: ImageMetadata;
  image_ids: string[]; // should save image_ids
  images: ImageMetadata &
    {
      manualAdjustment?: boolean;
    }[];
  direction: 'row' | 'column';
  justify: 'center' | 'start' | 'end';
  align: 'center' | 'start' | 'end';
  type: ELEMENT_TYPE.IMAGE;
  default_width: number;
  default_height: number;
}

class ImageElement extends BaseElement {
  public default_value: string;
  public label: string;
  public is_show_label: boolean;
  public placeholder: string;
  public multiple: boolean;
  public default_width: number;
  public default_height: number;
  public images: ImageElementProps['images'];
  public default_image: ImageElementProps['images'][0];
  public metadata: ImageElementProps['metadata'];
  public image_ids: ImageElementProps['image_ids'];
  public direction: ImageElementProps['direction'];
  public align: ImageElementProps['align'];
  public justify: ImageElementProps['justify'];
  public is_editable_value: boolean;

  constructor(props: ImageElementProps) {
    super(props);
    const {
      default_value,
      label,
      is_show_label,
      placeholder,
      multiple,
      images = [],
      metadata,
      direction = 'row',
      align = 'center',
      justify = 'center',
      is_editable_value,
      image_ids,
    } = props;

    this.default_value = default_value;
    this.label = label;
    this.is_show_label = is_show_label;
    this.placeholder = placeholder;
    this.multiple = multiple;
    this.images = images;
    this.direction = direction;
    this.align = align;
    this.justify = justify;
    this.default_width = props.default_width || metadata?.width || 100;
    this.default_height = props.default_height || metadata?.height || 100;
    this.is_editable_value = is_editable_value;
    if (metadata?.image_ids) {
      this.image_ids = metadata?.image_ids;
    }
    if (metadata?.images) {
      this.images = metadata?.images;
      if (default_value) {
        const default_image = this.images.find((it) => it.id === default_value);
        if (default_image) this.default_image = default_image;
      }
    }
    if (metadata?.default_image) {
      this.default_image = metadata?.default_image;
    }
    if (!metadata?.image_ids && default_value) {
      this.image_ids = [default_value];
    }
    if (image_ids) {
      this.image_ids = image_ids;
    }
  }

  public setValue(value: {
    images: ImageElementProps['images'];
    direction?: ImageElementProps['direction'];
    align?: ImageElementProps['align'];
    justify?: ImageElementProps['justify'];
  }) {
    if (this.images?.length > 0 && this.default_value) {
      const default_image = this.images.find((it) => it.id === this.default_value);
      if (default_image) this.default_image = default_image;
    }
    if (value.images) {
      this.images = value.images;
      this.image_ids = value.images.map((it) => it.id);
    }
    if (value.direction) this.direction = value.direction || 'row';
    if (value.align) this.align = value.align || 'center';
    if (value.justify) this.justify = value.justify || 'center';
  }

  public render(): string {
    if (this.is_hidden) return '';
    const layoutStyle =
      this.direction === 'row'
        ? 'display: flex; flex-direction: row;'
        : 'display: flex; flex-direction: column;';
    const imagesHtml = this.images
      .map(
        (image) =>
          `<span style="margin: 5px;">
            <img src="${image.src}" alt="${this.label}" crossOrigin="anonymous" style="width: ${image.width}px; height: ${image.height}px;" />
          </span>`
      )
      .join(' ');

    const content = this.is_show_label
      ? `${
          this.label
        }: <div style="${layoutStyle}; width: 100%; min-height: 200px; display: flex; ${aligntoStyle(
          this.align,
          this.direction,
          'align'
        )} ${aligntoStyle(this.justify, this.direction, 'justify')}">${imagesHtml}</div>`
      : `<div style="${layoutStyle}; width: 100%; min-height: 200px; display: flex; ${aligntoStyle(
          this.align,
          this.direction,
          'align'
        )} ${aligntoStyle(this.justify, this.direction, 'justify')}">${imagesHtml}</div>`;

    return this.withHighlight(content);
  }

  toJSON() {
    const json = Omit(this, ['images', 'parent', 'highlight']);
    json.metadata = {
      image_ids: this.images.map((it) => it.id),
      default_image: this.default_image, // need to save default image to edit in future
      images: this.images.map((it) => Omit(it, ['src'])),
    };
    return json;
  }
}

export default ImageElement;
